import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Check, Loader2, Sparkles, ChevronDown, Briefcase, Users, GraduationCap, CalendarCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const SUBJECTS = [
  { id: "consulting", label: "Consulting", icon: Briefcase, description: "Technical guidance & architecture review" },
  { id: "partnership", label: "Partnership", icon: Users, description: "Collaboration & joint ventures" },
  { id: "mentorship", label: "Mentorship", icon: GraduationCap, description: "Career guidance & coaching" },
  { id: "event", label: "Event Invitation", icon: CalendarCheck, description: "Speaking, workshops, conferences" },
];

const TONES = [
  { id: "corporate", label: "Professional", icon: "💼" },
  { id: "friendly", label: "Friendly", icon: "😊" },
  { id: "minimal", label: "Concise", icon: "✂️" },
];

export const ContactForm = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  // AI Rewrite state
  const [showAiHelper, setShowAiHelper] = useState(false);
  const [selectedTone, setSelectedTone] = useState("corporate");
  const [isRewriting, setIsRewriting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingRef = useRef(null);

  useEffect(() => {
    return () => { if (typingRef.current) clearInterval(typingRef.current); };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "message" && isTyping) {
      clearInterval(typingRef.current);
      setIsTyping(false);
    }
  };

  const selectSubject = (subjectId) => {
    setForm((prev) => ({ ...prev, subject: subjectId }));
    if (errors.subject) setErrors((prev) => ({ ...prev, subject: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.email.trim()) {
      errs.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = "Please enter a valid email address";
    }
    if (!form.message.trim()) {
      errs.message = "Please write your message";
    } else if (form.message.trim().length < 20) {
      errs.message = "Please provide more details (at least 20 characters)";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleRewritePro = async () => {
    if (!form.message.trim()) {
      setErrors((prev) => ({ ...prev, message: "Write something first to enhance" }));
      return;
    }

    setIsRewriting(true);
    setServerError("");
    if (typingRef.current) clearInterval(typingRef.current);

    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/rewrite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: form.message, tone: selectedTone }),
      });
      if (!res.ok) throw new Error("Rewrite failed");
      const data = await res.json();
      const fullText = data.rewritten_text;

      // Typewriter effect
      let i = 0;
      setIsTyping(true);
      setForm((prev) => ({ ...prev, message: "" }));
      typingRef.current = setInterval(() => {
        i++;
        const partial = fullText.slice(0, i);
        setForm((prev) => ({ ...prev, message: partial }));
        if (i >= fullText.length) {
          clearInterval(typingRef.current);
          setIsTyping(false);
        }
      }, 12);
    } catch {
      setServerError("AI service unavailable. You can still submit your message as-is.");
    } finally {
      setIsRewriting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    setStatus("submitting");
    try {
      const selectedSubject = SUBJECTS.find(s => s.id === form.subject);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: selectedSubject?.label || form.subject || "General",
        message: form.message.trim(),
      };
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Submission failed");
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
      setErrors({});
    } catch {
      setStatus("idle");
      setServerError("Something went wrong. Please try again.");
    }
  };

  const handleClose = () => {
    if (status !== "submitting") {
      setStatus("idle");
      setErrors({});
      setServerError("");
      if (typingRef.current) clearInterval(typingRef.current);
      setIsTyping(false);
      setShowAiHelper(false);
      setSelectedTone("corporate");
      onClose();
    }
  };

  const inputCls = (field) =>
    `w-full bg-zinc-900/50 border text-white placeholder-zinc-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/10 focus:border-zinc-600 transition-all duration-200 ${errors[field] ? "border-red-500/50" : "border-zinc-800/80"}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="contact-modal-overlay"
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-modal"
            className="relative w-full max-w-lg bg-[#0C0C0D] border border-zinc-800/60 rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-800/40">
              <div>
                <h3 className="text-lg font-semibold text-white">Get in Touch</h3>
                <p className="text-xs text-zinc-500 mt-0.5">I'd love to hear from you</p>
              </div>
              <button
                data-testid="contact-close-btn"
                onClick={handleClose}
                className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors duration-200"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {status === "success" ? (
              <div data-testid="contact-success" className="px-6 py-14 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                  <Check size={24} className="text-emerald-400" strokeWidth={2} />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Message Sent!</h4>
                <p className="text-sm text-zinc-500 max-w-xs mx-auto leading-relaxed">
                  Thank you for reaching out. I'll get back to you as soon as possible.
                </p>
                <button
                  data-testid="contact-success-close"
                  onClick={handleClose}
                  className="mt-8 inline-flex items-center px-6 py-2.5 bg-white text-black hover:bg-zinc-200 rounded-full text-sm font-medium transition-colors duration-200"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-5">
                {/* Name */}
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-zinc-400 mb-1.5">
                    Full Name
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    data-testid="contact-name-input"
                    placeholder="Your name"
                    className={inputCls("name")}
                  />
                  {errors.name && (
                    <p data-testid="error-name" className="text-xs text-red-400 mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-zinc-400 mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid="contact-email-input"
                    placeholder="your@email.com"
                    className={inputCls("email")}
                  />
                  {errors.email && (
                    <p data-testid="error-email" className="text-xs text-red-400 mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Subject Selection */}
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    What brings you here? <span className="text-zinc-600">(optional)</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {SUBJECTS.map((subject) => {
                      const Icon = subject.icon;
                      const isSelected = form.subject === subject.id;
                      return (
                        <button
                          key={subject.id}
                          type="button"
                          onClick={() => selectSubject(subject.id)}
                          data-testid={`contact-subject-${subject.id}`}
                          className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all duration-200 ${
                            isSelected
                              ? "bg-white/5 border-white/20 ring-1 ring-white/10"
                              : "bg-zinc-900/30 border-zinc-800/60 hover:border-zinc-700"
                          }`}
                        >
                          <div className={`p-2 rounded-lg ${isSelected ? "bg-white/10" : "bg-zinc-800/50"}`}>
                            <Icon size={16} className={isSelected ? "text-white" : "text-zinc-500"} strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isSelected ? "text-white" : "text-zinc-400"}`}>
                              {subject.label}
                            </p>
                            <p className="text-xs text-zinc-600 truncate">{subject.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="contact-message" className="block text-sm text-zinc-400 mb-1.5">
                    Your Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    data-testid="contact-message-input"
                    placeholder="Tell me about your project, idea, or how I can help..."
                    className={`${inputCls("message")} resize-none`}
                  />
                  {errors.message && (
                    <p data-testid="error-message" className="text-xs text-red-400 mt-1">{errors.message}</p>
                  )}
                </div>

                {/* AI Helper - Collapsible */}
                <div className="border border-zinc-800/60 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowAiHelper(!showAiHelper)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-800/20 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className="text-amber-400/80" />
                      <span className="text-sm text-zinc-400">AI Rewrite Pro</span>
                    </div>
                    <ChevronDown 
                      size={16} 
                      className={`text-zinc-600 transition-transform duration-200 ${showAiHelper ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  <AnimatePresence>
                    {showAiHelper && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-zinc-800/40">
                          <p className="text-xs text-zinc-500">
                            Let AI polish your message. Select a tone:
                          </p>
                          
                          {/* Tone Selection */}
                          <div className="flex gap-2">
                            {TONES.map((tone) => (
                              <button
                                key={tone.id}
                                type="button"
                                onClick={() => setSelectedTone(tone.id)}
                                data-testid={`contact-tone-${tone.id}`}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all duration-200 ${
                                  selectedTone === tone.id
                                    ? "bg-amber-500/10 border-amber-500/30 text-amber-400"
                                    : "bg-zinc-900/50 border-zinc-800 text-zinc-500 hover:border-zinc-700"
                                }`}
                              >
                                <span>{tone.icon}</span>
                                {tone.label}
                              </button>
                            ))}
                          </div>

                          {/* Enhance Button */}
                          <button
                            type="button"
                            onClick={handleRewritePro}
                            disabled={isRewriting || isTyping || !form.message.trim()}
                            data-testid="contact-enhance-btn"
                            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500/90 hover:bg-amber-500 text-black disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200"
                          >
                            {isRewriting || isTyping ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                {isTyping ? "Writing..." : "Enhancing..."}
                              </>
                            ) : (
                              <>
                                <Sparkles size={14} />
                                Enhance Message
                              </>
                            )}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Server error */}
                {serverError && (
                  <p data-testid="contact-error" className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    {serverError}
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "submitting" || isRewriting || isTyping}
                  data-testid="contact-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 rounded-full font-medium text-sm transition-colors duration-200"
                >
                  {status === "submitting" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" strokeWidth={1.5} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={14} strokeWidth={1.5} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
